import { useAuthMe } from '@h2hspark/auth-ui';
import { cn, MAIN_CONTENT_ID, SkipToMainLink } from '@h2hspark/ui';
import * as React from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';

import { useMemberSession } from '@/auth/auth-provider';

import { AccountClaimedConfirmation } from '../domains/account-claim/claimed-confirmation-panel';
import { SiteAccountActions } from '../domains/cms/site-chrome-panel';
import { useMyConnectionRequests } from '../domains/connections/api';
import { hasPendingRequests } from '../domains/connections/constants';
import { useMyFollowUps } from '../domains/follow-up/api';
import { hasOutstandingFollowUps } from '../domains/follow-up/constants';
import { useMyConversations } from '../domains/messages/api';
import { hasUnreadThreads } from '../domains/messages/constants';

import {
  activeDestinationFor,
  BADGED_DESTINATION,
  CONNECTIONS_DESTINATION,
  MESSAGES_DESTINATION,
  MEMBER_DESTINATIONS,
  PROFILE_DESTINATION,
} from './destinations';
import { MemberAvatarLink } from './member-avatar';
import { MemberFooter } from './member-footer';
import { PhoneTopBar } from './phone-top-bar';
import { MEMBER_SHELL_MAIN_PADDING_CLASS } from './pinned-bar-geometry';
import { OfflineNotice } from './pwa/offline-notice';
import { UnsupportedBrowserNotice } from './pwa/unsupported-browser-notice';
import { useMemberAppInstallability } from './pwa/use-member-app-installability';
import { useScrollMemory } from './use-scroll-memory';

/**
 * The member app's shell — **one application, two chromes** (`MEM-042`).
 *
 * ## What the navigation holds, and the ruling behind it
 *
 * **Discover · Your Events · Messages · Connections**, at every width, plus **You** on the phone
 * — Sumit's ruling of 2026-09-11 (*"agree"*), which departs from the specification's
 * `MEM-SHL-002` and `MEM-SHL-003` and was confirmed before it was built. Matches and Follow-up
 * moved inside Your Events, as the 10 September prototype draws it. **Messages is written in its
 * place and switched off** (`MESSAGES_IN_NAVIGATION` in `destinations.ts`) until messaging
 * exists, so the bar never links to an empty page. Both chromes read `MEMBER_DESTINATIONS`, so a
 * destination is added once or not at all — two lists drift by addition, and the drift is silent.
 *
 * ## The desktop header (`MEM-042` AC-3)
 *
 * `Web App.dc.html` lines 218–229: a sticky white bar, `height:64px`, a hairline
 * `--border-subtle` rule, content at `max-width:1180px; padding:0 32px; gap:32px`. The **logo
 * image** at 20px (the design's `assets/logotype.png`, which is `/brand/logotype.png` byte for
 * byte), then the items as pills — `font-brand 700 12px`, `padding:9px 11px`, an 18px Lucide icon,
 * `--purple-50` behind the active one and `--brand-primary` on it, `--text-muted` otherwise — then,
 * at the right, the member's 38px gradient avatar, which opens the profile. **No bell**: it has
 * nothing behind it, and Sumit ruled it out for now (`handoff-corrections.md` #120). No bottom
 * bar either — a fixed bar across a 1400px window is a phone control that has been stretched.
 *
 * The prototype's logo leaves the app for the marketing landing page, and so does this one: it is
 * the site's mark, and the landing is where the site's mark goes. Discover is one item away.
 *
 * ## The phone bar (`MEM-042` AC-2)
 *
 * A fixed bottom bar, within thumb reach, because members use this standing up and one-handed in a
 * room full of people: the same destinations, and **You** in the slot the desktop gives the avatar.
 *
 * ## Where the member is
 *
 * The owning destination is marked on every page beneath it (`activeDestinationFor`): Your Events
 * on an event's matches, Connect and follow-up; Discover on an event page. The prototype marks
 * nothing on the account pages, which `handoff-corrections.md` #120 corrects.
 *
 * ## The badges (`MEM-042` AC-5, AC-7, `MEM-055` AC-4)
 *
 * Three dots, none with a number, each bound to its destination by path so reordering either
 * chrome cannot detach one:
 *
 *   - **Your Events**, when a follow-up is outstanding — the item that now holds follow-up;
 *   - **Messages**, when any thread holds a message the member has received and not opened;
 *   - **Connections**, when at least one incoming request is waiting on an answer.
 *
 * A count is a second place the total is stated and a second place it can be wrong; the dot says
 * *there is something* and the screen says what.
 *
 * **The third arrived on 2026-09-23 and departs from the ruling that shaped the other two**
 * (`MEM-042` AC-7, TW 44159923). On 11 September Sumit put the request figure on the Connections
 * page's own *Requests* control and kept it out of the navigation; on 23 September, approving the
 * dot, he took the other side: *"a waiting connection request will show a dot on the Connections
 * item in the navigation, so you can see it from anywhere in the app."* A member who is not
 * already on that page had no way of learning a request had arrived, which is the exact problem
 * the other two dots exist to solve. **The page's count is untouched** — the dot and the count
 * are on different screens and answer different questions.
 *
 * All three are `MEM-040` AC-1's **first device**, not a fourth: ADR 052 is explicit that *"the
 * unread count is a navigation badge … so no fourth device is added"*, and a third badge is a
 * third instance of the first device rather than a new one. There is still no bell and no
 * notification feed.
 *
 * ## The footer
 *
 * The in-app footer the prototype draws beneath every desktop screen, from `member-footer.tsx`,
 * which renders the marketing site's CMS-driven footer with the app's own columns. Desktop only.
 *
 * ## Drawn for everybody
 *
 * Every destination but Discover is gated, and a signed-out visitor reaching one gets the Sign-in
 * required interstitial (`MEM-019` AC-3) rather than a redirect — so the chrome is drawn whether
 * or not there is a session. A shell that changes shape when you sign in tells a visitor the
 * product is hiding something.
 */
export function MemberLayout(): React.ReactElement {
  useMemberAppInstallability();
  // `MEM-042` AC-6. In the shell rather than in each list, because every list is inside it and a
  // hook that four screens have to remember to call is a hook one screen will forget.
  useScrollMemory();

  // Only for a signed-in member. The chrome is drawn for everybody (see above), but a signed-out
  // visitor has no follow-ups to count and asking would be a 401 on every page load.
  const { user } = useAuthMe();
  const { session, status: sessionStatus } = useMemberSession();
  const signedIn = user !== null && user !== undefined;
  const followUps = useMyFollowUps({ enabled: signedIn });
  // `MEM-055` AC-4. Same gate as the follow-ups above, for the same reason: a signed-out visitor
  // has no threads and asking would be a 401 on every page load.
  const conversations = useMyConversations({ enabled: signedIn });
  const showUnreadDot = hasUnreadThreads(conversations.data?.conversations);
  /*
    `MEM-042` AC-7, TW 44159923. Same gate as the two above it, for the same reason.

    **The Connections page's own hook, not a second one and not a poll.** `useMyConnectionRequests`
    is keyed `['connections', 'requests']`, so the shell and every screen that shows the queue —
    the Requests panel, `/app/connections/requests`, the per-event page — are one entry in one
    cache. Mounting it here adds a read on the first member page of a session and nothing after
    that: it refreshes when React Query considers the entry stale, which is 30 seconds
    (`createQueryClient`), and only on a mount or a manual invalidation, because
    `refetchOnWindowFocus` is off and nothing sets `refetchInterval`. Answering a request
    invalidates the same key (`useAnswerConnectionRequest`), so the dot clears on the press rather
    than on the next timer.

    A `setInterval` in the shell would have been a request every N seconds on every screen for
    every signed-in member, forever, to learn something that changes a few times a day.
  */
  const connectionRequests = useMyConnectionRequests({ enabled: signedIn });
  const showRequestDot = hasPendingRequests(connectionRequests.data?.requests);
  // The avatar is **the member profile**, not the session, and it is `member-avatar.tsx`'s
  // (TW 44131893, TW 44145091). It reads its own profile and draws nothing without a session, so
  // the shell holds none of it — the marketing header draws the same component.
  const showBadge =
    followUps.data !== undefined && hasOutstandingFollowUps(followUps.data.followUps);

  const desktopDestinations = MEMBER_DESTINATIONS.filter((destination) => destination.onDesktop);
  const { pathname } = useLocation();
  const active = activeDestinationFor(pathname);

  return (
    <div className="flex min-h-screen flex-col bg-background">
      {/* Before the header, because the header is the chrome it skips (`PLT-007` AC-3). It earns
          its place on the phone too: the bottom bar is fixed and therefore last in the DOM, so a
          keyboard user on a long Discover list still has the whole page between them and the
          content they arrived for. */}
      <SkipToMainLink />
      {/* The phone's brand bar, on the four tab screens only — see `phone-top-bar.tsx`. */}
      <PhoneTopBar pathname={pathname} />
      {/* ── The desktop header. `md` and up; the phone keeps its bottom bar. ── */}
      <header className="sticky top-0 z-30 hidden border-b border-border-subtle bg-card md:block print:hidden">
        <div className="mx-auto flex h-16 w-full max-w-app-desktop items-center gap-8 px-8">
          <Link to="/" className="flex shrink-0 items-center" aria-label="H2HSpark home">
            <img
              src="/brand/logotype.png"
              alt=""
              aria-hidden
              className="h-5 w-auto"
              width={1787}
              height={298}
            />
          </Link>

          <nav
            aria-label="Member app"
            className="ml-2 flex min-w-0 items-center gap-0.5 overflow-x-auto"
          >
            {desktopDestinations.map((destination) => {
              const isActive = active === destination.to;
              return (
                <Link
                  key={destination.to}
                  to={destination.to}
                  aria-current={isActive ? 'page' : undefined}
                  className={cn(
                    'inline-flex flex-none items-center gap-1.5 whitespace-nowrap rounded-pill px-[11px] py-[9px] font-brand text-xs font-bold transition-colors',
                    isActive
                      ? 'bg-brand-50 text-primary'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  <span className="relative inline-flex">
                    <destination.Icon className="h-[18px] w-[18px]" aria-hidden />
                    {badgedFor(destination.to, showBadge, showUnreadDot, showRequestDot) ? (
                      <span
                        // `MEM-042` AC-5 and AC-7, and `MEM-055` AC-4. Decorative to a screen
                        // reader — the accessible name is the label, and what is waiting is on
                        // the screen.
                        aria-hidden
                        className="absolute -right-[5px] -top-[3px] h-2 w-2 rounded-full border-2 border-card bg-brand-accent"
                      />
                    ) : null}
                  </span>
                  {destination.label}
                </Link>
              );
            })}
          </nav>

          {/* `MEM-042` AC-3's avatar, at the right — and the desktop's only route to Your account,
              which is why You is not also a navigation item. It opened `/app/profile` until
              2026-09-12; that screen is gone (Sumit: *"we don't need to redirect the old profile
              url, it can just be removed"*) and both doors now open `/app/account`. */}
          {/* Signed out, the same spot holds the public header's *Log in* and *Get started*
              (`MEM-043` AC-6, TW 44206359) — drawn by `SiteAccountActions`, which is the public
              header's own rule, so the two headers cannot disagree about them. Until the session
              is known they are shown, exactly as the public header shows them while it waits: a
              visitor is the state a first paint is right about, and a member sees them swap once
              for the avatar. */}
          <div className="ml-auto flex items-center gap-4">
            {sessionStatus === 'authenticated' ? (
              <MemberAvatarLink current={active === PROFILE_DESTINATION} />
            ) : (
              <SiteAccountActions />
            )}
          </div>
        </div>
      </header>

      {/* **Fluid below `md`** — the whole width, with the phone's 16px gutters — and the one desktop
          canvas above it. It used to be a 390px phone column centred on a muted page at every width
          under 768, which at 560px left the content boxed with a blank gutter each side; Sumit's
          ruling is one app with a responsive layout, and a phone layout that stops at 390px is the
          phone design stretched the other way. */}
      <div className="mx-auto flex w-full flex-1 flex-col md:max-w-app-desktop">
        {/* The bottom padding is `MEMBER_SHELL_MAIN_PADDING_CLASS` rather than a literal,
            because it is half of the arithmetic that keeps a screen's last card out from
            under a pinned bar — see `pinned-bar-geometry.ts`, which owns both halves. */}
        {/* `tabIndex={-1}` so the skip link moves focus here and not merely the scroll position;
            see `SkipToMainLink`. */}
        <main
          id={MAIN_CONTENT_ID}
          tabIndex={-1}
          className={cn('flex-1 px-4 pt-6 md:px-8 md:pt-9', MEMBER_SHELL_MAIN_PADDING_CLASS)}
        >
          {/*
            The connectivity state (`MEM-043` AC-3), above the content of every screen rather than
            on the screens that fetch.

            In the shell for the reason `useScrollMemory` is: a notice each screen has to remember
            to render is a notice one screen will forget, and the screen that forgets is the one
            drawing a cached roster as though it were live. It renders nothing when the browser is
            online — there is no green counterpart, because `navigator.onLine` being true proves
            nothing (`pwa/connectivity.ts`).
          */}
          <OfflineNotice />
          {/*
            What an unsupported browser is told (`PLT-008` AC-2), in the shell for the same reason
            the connectivity state is: a notice each screen has to remember is a notice one screen
            will forget.

            **Above `Outlet`, never around it.** The criterion is that reading, registration and
            checkout are never blocked, and the way they would get blocked is a well-meaning gate
            here. This is a sibling of the content, so an unsupported browser gets the sentence
            *and* the screen.
          */}
          <UnsupportedBrowserNotice />
          {/*
            The **Account claimed** confirmation (`USR-009` AC-3), in the shell for the reason the
            two notices above it are: the claim resolves inside a sign-in, on the server, and the
            member is on their way to whatever they signed in *for*. A confirmation that was a
            route would have to take that destination away from them.

            Only for a signed-in member, for `useMyFollowUps`'s reason above — a signed-out
            visitor has nothing claimed and asking would be a 401 on every page load.
          */}
          <AccountClaimedConfirmation enabled={session !== null} />
          <Outlet />
        </main>
      </div>

      <MemberFooter />

      {/* ── The phone bar. Hidden from `md` up, where the header has the same destinations. ── */}
      <nav
        aria-label="Member app"
        className="fixed inset-x-0 bottom-0 z-30 w-full border-t bg-background/95 pb-[env(safe-area-inset-bottom)] backdrop-blur md:hidden print:hidden"
      >
        {/* The bar spans the viewport; its items sit centred within `max-w-lg`, so on a large
            phone or a small tablet they stay within a thumb's reach of each other rather than
            spreading to the corners. */}
        <ul
          className="mx-auto grid w-full max-w-lg"
          style={{ gridTemplateColumns: `repeat(${String(MEMBER_DESTINATIONS.length)}, 1fr)` }}
        >
          {MEMBER_DESTINATIONS.map((destination) => {
            const isActive = active === destination.to;
            return (
              <li key={destination.to}>
                <Link
                  to={destination.to}
                  aria-current={isActive ? 'page' : undefined}
                  className={cn(
                    'flex flex-col items-center gap-1 py-2.5 text-[11px] transition-colors',
                    isActive
                      ? 'font-bold text-primary'
                      : 'font-medium text-text-subtle hover:text-foreground',
                  )}
                >
                  <span className="relative">
                    <destination.Icon className="h-5 w-5" aria-hidden />
                    {badgedFor(destination.to, showBadge, showUnreadDot, showRequestDot) ? (
                      <span
                        // `MEM-042` AC-5 and AC-7. Decorative to a screen reader — the accessible
                        // name is on the label below, and the count is on the screen.
                        aria-hidden
                        className="absolute -right-1.5 -top-0.5 h-2 w-2 rounded-full border-2 border-background bg-brand-accent"
                      />
                    ) : null}
                  </span>
                  <span>{destination.label}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>
    </div>
  );
}

/**
 * **Which destinations carry a dot, and what each dot is about** (`MEM-042` AC-5 and AC-7,
 * `MEM-055` AC-4).
 *
 * One function rather than a condition written three times, because the two chromes are two
 * renderings of one list and a badge that appeared on the phone and not on the desktop would be
 * the drift `destinations.ts` exists to prevent. Matched on the destination's path, so reordering
 * either bar cannot detach a dot from what it is about.
 */
function badgedFor(
  destination: string,
  outstandingFollowUps: boolean,
  unreadThreads: boolean,
  pendingRequests: boolean,
): boolean {
  if (destination === BADGED_DESTINATION) return outstandingFollowUps;
  if (destination === MESSAGES_DESTINATION) return unreadThreads;
  if (destination === CONNECTIONS_DESTINATION) return pendingRequests;
  return false;
}
